import React, { useRef, useState } from 'react'

import classNames from 'classnames'

import { ButtonType } from 'src/apollo/sites'
import { BrandedButton } from 'src/shared/components/common/button'
import DropdownArrow from 'src/shared/components/common/dropdown/DropdownArrow'
import Portal from 'src/shared/components/common/modal/Portal'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { t } from 'src/translations'

type Option = {
  name: string
  value: string
  selected: boolean
}

type Props = {
  name: string
  options: (Option & { children: Option[] })[]
  onChange: (value: string | string[]) => void
  onClear: () => void
  alignment: 'left' | 'right'
}

export const Filter = (props: Props) => {
  const [expanded, setExpanded] = useState(false)
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const localFilters = useRef<Set<string>>(new Set())

  const Wrapper = isMobile ? Portal : React.Fragment

  const toggle = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setExpanded((expanded) => !expanded)
  }

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // ignore clicks inside of the menu
    if (
      !isMobile &&
      !event.currentTarget.contains(event.relatedTarget as Node)
    ) {
      setExpanded(false)
    }
  }

  return (
    <div className='filter' aria-label={props.name} onBlur={onBlur}>
      <button
        type='button'
        className='filterLabel'
        aria-expanded={expanded}
        onClick={toggle}
      >
        {props.name}
        <span className='arrow'>
          <DropdownArrow />
        </span>
      </button>
      {expanded ? (
        <Wrapper>
          <div
            className={classNames('filterContent', {
              left: props.alignment === 'left'
            })}
            role='group'
          >
            <div className='filterOptionsContainer'>
              {props.options.map((option) => {
                const parentRef = React.createRef<HTMLInputElement>()
                const childRefs = option.children.map(() =>
                  React.createRef<HTMLInputElement>()
                )
                const childValues = option.children.map((c) => c.value)

                return (
                  <React.Fragment key={option.value}>
                    <label htmlFor={option.value} className='filterOption bold'>
                      <input
                        ref={parentRef}
                        type='checkbox'
                        id={option.value}
                        defaultChecked={option.selected}
                        onChange={(e) => {
                          e.stopPropagation()
                          childRefs.forEach((c) => {
                            if (c.current) c.current.checked = e.target.checked
                          })

                          if (localFilters.current.has(option.value)) {
                            childValues.forEach((v) =>
                              localFilters.current.delete(v)
                            )
                          } else {
                            childValues.forEach((v) =>
                              localFilters.current.add(v)
                            )
                          }
                        }}
                      />
                      {option.name}
                    </label>
                    {(option.children || [option]).map((childOption, index) => (
                      <label
                        key={childOption.value}
                        htmlFor={childOption.value}
                        className='indent filterOption '
                      >
                        <input
                          ref={childRefs[index]}
                          type='checkbox'
                          id={childOption.value}
                          defaultChecked={childOption.selected}
                          onChange={(e) => {
                            e.stopPropagation()

                            if (localFilters.current.has(childOption.value)) {
                              localFilters.current.delete(childOption.value)
                            } else {
                              localFilters.current.add(childOption.value)
                            }

                            if (
                              childValues &&
                              parentRef.current &&
                              localFilters.current.size !== childValues.length
                            ) {
                              parentRef.current.checked = false
                            } else if (
                              childValues &&
                              parentRef.current &&
                              localFilters.current.size === childValues.length
                            ) {
                              parentRef.current.checked = true
                            }
                          }}
                        />
                        {childOption.name}
                      </label>
                    ))}
                  </React.Fragment>
                )
              })}
            </div>
            <div className='buttons'>
              <BrandedButton
                className='button'
                variant={ButtonType.Secondary}
                onClick={(e: React.MouseEvent) => {
                  props.onChange(Array.from(localFilters.current))
                  localFilters.current = new Set()
                  toggle(e)
                }}
              >
                {t('filter.apply')}
              </BrandedButton>
              <BrandedButton
                className='button'
                variant={ButtonType.Secondary}
                onClick={(e: React.MouseEvent) => {
                  props.onClear()
                  toggle(e)
                }}
              >
                {t('filter.reset')}
              </BrandedButton>
            </div>
          </div>
        </Wrapper>
      ) : null}
    </div>
  )
}
